import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './Analysis.css';
import { useOpportunityAnalyticsContext } from '..';
import { getAccessToken } from 'src/utils/token-service';
import { useMsal } from '@azure/msal-react';

interface Request {
  id: number;
  status: string;
  dropdown1: string;
  dropdown2: string;
  startDate: string;
  endDate: string;
  description: string;
  userId: string;
}

interface AnalysisProps {
  requests: Request[];
  setRequests: React.Dispatch<React.SetStateAction<Request[]>>;
}

type DropdownOptions = {
  [key: string]: string[];
};

const Analysis: React.FC = () => {
  const { requests, setRequests } = useOpportunityAnalyticsContext();
  const { instance } = useMsal();

  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);  // Loading state
  const [formData, setFormData] = useState({
    dropdown1: '',
    dropdown2: '',
    startDate: '',
    endDate: '',
    description: '',
  });

  const [dropdownOptions, setDropdownOptions] = useState<DropdownOptions>({});
  const [dropdown2Options, setDropdown2Options] = useState<string[]>([]);

  const [errors, setErrors] = useState({
    dropdown1: '',
    dropdown2: '',
    startDate: '',
    endDate: '',
  });

  const apiUrl = 'https://api-dv.brightspeed.com/genai-studio/parent_child_categories/';
  const insertRequestUrl = 'https://api-dv.brightspeed.com/genai-studio/insert_request_status/';

  useEffect(() => {
    const fetchData = async () => {
      const accessToken = await getAccessToken();
      try {
        const response = await fetch(apiUrl, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        const data = await response.json();
        const parsedData = typeof data === 'string' ? JSON.parse(data) : data;
        const options: DropdownOptions = {};
        Object.keys(parsedData).forEach((parent) => {
          options[parent] = parsedData[parent];
        });

        setDropdownOptions(options);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    if (formData.dropdown1) {
      setDropdown2Options(dropdownOptions[formData.dropdown1] || []);
      setFormData({ ...formData, dropdown2: '' });
    }
  }, [formData.dropdown1, dropdownOptions]);

  const navigate = useNavigate();

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const validateForm = () => {
    const newErrors: any = {};
    if (!formData.dropdown1) newErrors.dropdown1 = 'Parent Intent is mandatory';
    if (!formData.dropdown2) newErrors.dropdown2 = 'Child Intent is mandatory';
    if (!formData.startDate) newErrors.startDate = 'Start Date is mandatory';
    if (!formData.endDate) newErrors.endDate = 'End Date is mandatory';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0; // If no errors, form is valid
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsLoading(true);  // Start loading

    const accounts = instance.getAllAccounts();
    const loggedInUserName = accounts && accounts.length > 0 ? accounts[0].username : '';
    const requestPayload = {
      description: formData.description,
      dropdown1: formData.dropdown1,
      dropdown2: formData.dropdown2,
      end_date: formData.endDate,
      start_date: formData.startDate,
      status: 'Completed',
      userid: loggedInUserName,
    };

    const accessToken = await getAccessToken();

    try {
      const response = await fetch(insertRequestUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(requestPayload),
      });

      const result = await response.json();
      if (response.ok) {
        setFormData({
          dropdown1: '',
          dropdown2: '',
          startDate: '',
          endDate: '',
          description: '',
        });
        setIsSubmitted(true);
        setTimeout(() => {
          setIsSubmitted(false);
        }, 1000);
      } else {
        console.error('Error submitting request:', result);
      }
    } catch (error) {
      console.error('Error submitting request:', error);
    } finally {
      setIsLoading(false);  // Stop loading
    }
  };

  return (
    <div className="container">
      <div className="card">
        <div className="card-header">
          <h2>Analysis</h2>
          <p>Please submit your request!</p>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="row">
            <div className="col form-group">
              <label className="form-label" htmlFor="dropdown1">
                Select Parent Intent
              </label>
              <select
                id="dropdown1"
                name="dropdown1"
                className="form-control"
                value={formData.dropdown1}
                onChange={handleInputChange}
              >
                <option value="">Select...</option>
                {Object.keys(dropdownOptions).map((parentKey) => (
                  <option key={parentKey} value={parentKey}>
                    {parentKey}
                  </option>
                ))}
              </select>
              {errors.dropdown1 && <p className="error-message">{errors.dropdown1}</p>}
            </div>
            <div className="col form-group">
              <label className="form-label" htmlFor="dropdown2">
                Select Child Intent
              </label>
              <select
                id="dropdown2"
                name="dropdown2"
                className="form-control"
                value={formData.dropdown2}
                onChange={handleInputChange}
              >
                <option value="">Select...</option>
                {dropdown2Options.map((option, index) => (
                  <option key={index} value={option}>
                    {option}
                  </option>
                ))}
              </select>
              {errors.dropdown2 && <p className="error-message">{errors.dropdown2}</p>}
            </div>
          </div>
          <div className="row">
            <div className="col form-group">
              <label className="form-label" htmlFor="startDate">
                Start Date
              </label>
              <input
                id="startDate"
                type="date"
                name="startDate"
                className="form-control"
                value={formData.startDate}
                onChange={handleInputChange}
              />
              {errors.startDate && <p className="error-message">{errors.startDate}</p>}
            </div>
            <div className="col form-group">
              <label className="form-label" htmlFor="endDate">
                End Date
              </label>
              <input
                id="endDate"
                type="date"
                name="endDate"
                className="form-control"
                value={formData.endDate}
                onChange={handleInputChange}
              />
              {errors.endDate && <p className="error-message">{errors.endDate}</p>}
            </div>
          </div>
          <div className="form-group">
            <label className="form-label" htmlFor="description">
              Describe more
            </label>
            <textarea
              id="description"
              name="description"
              className="form-control"
              value={formData.description}
              onChange={handleInputChange}
            ></textarea>
          </div>
          <div className="button-container">
            <button type="submit" className="button" disabled={isLoading}>
              Submit Request
            </button>
          </div>
        </form>

        {/* Spinner container */}
        {isLoading && (
          <div className="spinner-overlay">
            <div className="spinner"></div>
          </div>
        )}
      </div>

      {isSubmitted && (
        <div className="popup-overlay">
          <div className="popup-content">
            <p>Your form is submitted!</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default Analysis;
